import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  Typography,
} from "@mui/material";
import { Form } from "@shared/components/Form";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { TextField } from "@shared/components/TextField";
import { useForm } from "react-hook-form";
import {
  editUserEmailFormSchema,
  EditUserEmailFormSchema,
} from "./edit-user-email-form-helpers";
import { useAdminUpdateUserEmail } from "@queries/users/useAdminUpdateUserEmail";
import { useEffect } from "react";

type Props = {
  userId: string;
  userName: string | undefined;
  userEmail: string;
  onClose: () => void;
  open: boolean;
};

export const EditUserEmailModal = (props: Props) => {
  const { userId, userName, userEmail, onClose, open } = props;

  const editUserEmailFormMethods = useForm<EditUserEmailFormSchema>({
    defaultValues: {
      currentUserEmail: userEmail,
      newUserEmail: "",
    },
    resolver: yupResolver(editUserEmailFormSchema),
    reValidateMode: "onChange",
  });
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = editUserEmailFormMethods;

  const adminUpdateUserEmailMutation = useAdminUpdateUserEmail({
    onSuccess: () => {
      onClose();
    },
    onMutate: (data) => {
      reset({ currentUserEmail: data.email, newUserEmail: "" });
    },
    userId,
  });

  useEffect(() => {
    reset({ currentUserEmail: userEmail, newUserEmail: "" });
  }, [reset, userEmail]);

  const onSubmit = ({ newUserEmail }: EditUserEmailFormSchema) => {
    adminUpdateUserEmailMutation.mutate({
      email: newUserEmail,
    });
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle id="edit-user-email-dialog-title">
        Edit User Email
      </DialogTitle>
      <DialogContent id="edit-user-email-dialog-content">
        <Form
          onSubmit={handleSubmit(onSubmit)}
          id="edit-user-email-form"
          aria-disabled={adminUpdateUserEmailMutation.isPending}
        >
          <Box mb={2}>
            <Box mb={3}>
              <InputLabel>Name</InputLabel>
              {userName ? (
                <Typography>{userName}</Typography>
              ) : (
                <Typography color="secondary">Empty</Typography>
              )}
            </Box>
            <Box>
              <InputLabel>Current email</InputLabel>
              <Typography>{userEmail}</Typography>
            </Box>
          </Box>

          <TextField
            name={"userEmail"}
            label={"New Email"}
            inputProps={{ ...register("newUserEmail") }}
            placeholder="Email"
            fieldError={errors.newUserEmail}
            autoFocus
          />
        </Form>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="text"
          disabled={adminUpdateUserEmailMutation.isPending}
        >
          Cancel
        </Button>

        <SubmitButton
          type="submit"
          form="edit-user-email-form"
          isLoading={adminUpdateUserEmailMutation.isPending}
        >
          Edit
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
};
